import { ID, SubscriptionsType } from "@nova/constants"
import { AddOn, License, LicenseRecord } from "@nova/schemas"
import { createAsyncThunk } from "@reduxjs/toolkit"
import { HttpMethod } from "constants/server"
import { RootState } from "integrations/redux"
import { GenericRejectionResponseWithReason } from "integrations/redux/types"
import { setupApiProtection } from "tools/browser/makeCSRF"
import { isNonEmptyString } from "tools/strings/isNonEmptyString"
import { selectUserID } from "user/profile/selectors"

export const listLicensesForUser = createAsyncThunk<
    { licenses: License[]; addOns: AddOn[] },
    { ownerId: ID },
    GenericRejectionResponseWithReason
>("licenses/listLicenses", async (payload, thunkApi) => {
    const { ownerId } = payload

    if (!isNonEmptyString(ownerId)) {
        return thunkApi.rejectWithValue(false, {
            rejectionReason: "listLicensesForUser thunk requires a valid ownerId parameter",
        })
    }

    const protection = await setupApiProtection()
    const res = await fetch(`/api/user/${ownerId}/licenses/owned`, {
        method: HttpMethod.GET,
        headers: {
            ...protection.headers,
            "Content-Type": "application/json",
        },
    })
    const licenses = await res.json()
    return licenses
})

export const activateLicenses = createAsyncThunk<
    { licenses: License[]; addOns: License[] } | null,
    { activationKey: ID; ownerId: ID },
    GenericRejectionResponseWithReason
>("licenses/activateLicenses", async (payload, thunkApi) => {
    const { activationKey, ownerId } = payload

    if (!isNonEmptyString(ownerId)) {
        return thunkApi.rejectWithValue(false, {
            rejectionReason: "activateLicenses thunk requires a valid ownerId parameter",
        })
    }

    const res = await fetch(`/api/user/${ownerId}/licenses/owned?activationKey=${activationKey}`, {
        method: HttpMethod.PATCH,
        headers: {
            "Content-Type": "application/json",
        },
    })
    const licenses = await res.json()
    return licenses
})

export const verifyActivationKey = createAsyncThunk<
    boolean,
    { activationKey: ID },
    GenericRejectionResponseWithReason
>("licenses/activateLicenses", async (payload, thunkApi) => {
    const { activationKey } = payload

    if (!isNonEmptyString(activationKey)) {
        return thunkApi.rejectWithValue(false, {
            rejectionReason: "activateLicenses thunk requires a valid ownerId parameter",
        })
    }

    const protection = await setupApiProtection()
    const res = await fetch(`/api/license/unactivated/${activationKey}`, {
        method: HttpMethod.GET,
        headers: {
            ...protection.headers,
            "Content-Type": "application/json",
        },
    })
    const json = await res.json()
    return json.isValidKey
})

export const updateLicense = createAsyncThunk<
    { updatedLicense: License; revokedLicense: License },
    {
        licenseId: ID
        ownerId: ID
        attributes: {
            assigneeId?: string
            subscriptionType?: SubscriptionsType
            createdAt?: string
        }
    },
    GenericRejectionResponseWithReason
>("licenses/updateLicense", async (payload, thunkApi) => {
    const { licenseId, attributes, ownerId } = payload

    if (!isNonEmptyString(licenseId) || !isNonEmptyString(ownerId)) {
        return thunkApi.rejectWithValue(false, {
            rejectionReason: "updateLicense thunk requires a valid licenseId and ownerId parameter",
        })
    }

    const state = thunkApi.getState() as RootState
    const userId = selectUserID(state)

    const res = await fetch(`/api/license/${licenseId}?ownerId=${encodeURIComponent(ownerId)}`, {
        method: HttpMethod.PATCH,
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(attributes),
    })
    const license = await res.json()

    if (license.duplicateSubscriptionError) {
        return thunkApi.rejectWithValue(licenseId, {
            rejectionReason: "duplicateSubscription",
        })
    }
    if (license.error) {
        return thunkApi.rejectWithValue(false, {
            rejectionReason: "failed to update license",
        })
    }

    if (license.updatedLicense.ownerId !== userId) return null
    return license
})

export const assignPurchasedLicense = createAsyncThunk<
    { updatedLicense: LicenseRecord; revokedLicense: LicenseRecord },
    {
        subscriptionId: ID
        subscriptionType: SubscriptionsType
        assigneeId: ID
    },
    GenericRejectionResponseWithReason
>("licenses/assignPurchasedLicense", async (payload, thunkApi) => {
    const { subscriptionId, subscriptionType, assigneeId } = payload

    if (
        !isNonEmptyString(subscriptionId) ||
        !isNonEmptyString(subscriptionType) ||
        !isNonEmptyString(assigneeId)
    ) {
        return thunkApi.rejectWithValue(false, {
            rejectionReason:
                "assignPurchasedLicense thunk requires a valid subscriptionId and subscriptionType and assigneeId parameter",
        })
    }

    const protection = await setupApiProtection()
    const res = await fetch(
        `/api/user/${assigneeId}/licenses/assigned?subscriptionId=${subscriptionId}&subscriptionType=${subscriptionType}`,
        {
            method: HttpMethod.PATCH,
            headers: {
                ...protection.headers,
                "Content-Type": "application/json",
            },
        }
    )
    const license = await res.json()

    return license
})
